import { matchLocale, resolveLocale, type Locale } from "@tami/core/intl";

import { LOCALE_COOKIE, LOCALE_COOKIE_MAX_AGE_SECONDS } from "./locale-cookie";
import { localizedPath, parseLocalizedPublicPath } from "./locale-path";

/** Lê o cookie de escolha de idioma de um `document.cookie`. */
export function readLocaleCookie(cookieHeader: string): string | null {
  for (const part of cookieHeader.split(";")) {
    const [name, ...value] = part.trim().split("=");
    if (name === LOCALE_COOKIE) return decodeURIComponent(value.join("="));
  }
  return null;
}

/**
 * O idioma de quem está no navegador: a escolha gravada no cookie, senão a
 * lista de idiomas do navegador. É a mesma regra do servidor
 * (`install-request-locale.ts`), lida do lado de cá.
 */
export function detectClientLocale(
  cookieHeader: string,
  languages: readonly string[],
): Locale {
  const chosen = readLocaleCookie(cookieHeader);
  if (chosen) return resolveLocale(chosen);
  return matchLocale(languages);
}

/**
 * Grava a escolha de idioma e recarrega a página: trocar o idioma é raro, e
 * recarregar garante que nenhum texto lido antes fique no idioma antigo.
 *
 * Só recarrega se o cookie pegou: com cookie bloqueado, a página voltaria no
 * idioma antigo e a sincronização com a conta pediria a troca de novo, em
 * laço.
 *
 * Nas páginas públicas traduzidas o idioma é a URL: em vez de recarregar, vai
 * para a mesma página no idioma novo (`/demo` → `/en/demo`).
 */
export function chooseLocale(locale: Locale): void {
  document.cookie = `${LOCALE_COOKIE}=${encodeURIComponent(locale)}; path=/; max-age=${LOCALE_COOKIE_MAX_AGE_SECONDS}; samesite=lax`;
  if (readLocaleCookie(document.cookie) !== locale) return;
  const publicPage = parseLocalizedPublicPath(window.location.pathname);
  if (publicPage) {
    const target = localizedPath(publicPage.path, locale);
    window.location.assign(`${target}${window.location.search}${window.location.hash}`);
    return;
  }
  window.location.reload();
}
